import { format } from 'date-fns'
import { type Job } from '@/features/jobs/Job'

export type ConsultationPhase =
  | 'availability'
  | 'pick'
  | 'reschedule'
  | 'arrange'
  | 'booked'
  | 'live'
  | 'wrapUp'
  | 'complete'

export interface ConsultationState {
  phase: ConsultationPhase
  /**
   * Index into {@link consultationSteps}.
   */
  step: number
  title: string
  description: string
  /**
   * Whether this viewer is the one who has to pick the time.
   */
  canPickTime: boolean
}

export const consultationSteps = ['Schedule', 'Call', 'Wrap up', 'Complete']

/**
 * Whether the viewer is the one who has to pick the call time.
 *
 * @param job
 */
export function canPickConsultationTime(job: Job) {
  return Boolean(
    (job.isClaimedJob || job.isOnHold) && job.consultation?.isPendingBooking,
  )
}

/**
 * One reading of the consultation's status, call step and booking shared by
 * the step line and the column summary, so they never disagree about where
 * the call stands.
 *
 * @param job
 */
export function consultationState(job: Job): ConsultationState | null {
  const consultation = job.consultation
  if (!consultation) {
    return null
  }
  const client = job.championClient?.member.displayName ?? 'The client'
  const canPickTime = canPickConsultationTime(job)

  if (consultation.skipScheduling) {
    return {
      phase: 'arrange',
      step: 0,
      title: 'Arrange the call in chat',
      description: `Agree on a time with ${client} in chat, then join the call.`,
      canPickTime: false,
    }
  }

  if (consultation.isPendingAvailability) {
    return {
      phase: 'availability',
      step: 0,
      title: 'Waiting on availability',
      description: `${client} hasn't sent times that work yet.`,
      canPickTime: false,
    }
  }

  if (consultation.isPendingBooking) {
    const reschedule = consultation.previouslyBooked
    return {
      phase: reschedule ? 'reschedule' : 'pick',
      step: 0,
      title: reschedule ? 'Rescheduling requested' : 'Pick a call time',
      description: canPickTime
        ? `${client} sent times that work. Choose one to book the call.`
        : `${client} sent times that work; the pro picks one.`,
      canPickTime,
    }
  }

  switch (consultation.callStep) {
    case 'InProgress':
      return {
        phase: 'live',
        step: 1,
        title: 'Call in progress',
        description: 'The consultation is happening now.',
        canPickTime: false,
      }
    case 'Done':
      return {
        phase: 'wrapUp',
        step: 2,
        title: 'Finish the consultation',
        description: 'Start another call with the client, or complete the job.',
        canPickTime: false,
      }
    case 'Completed':
      return {
        phase: 'complete',
        step: 3,
        title: 'Consultation complete',
        description: 'The call is done and the room is closed.',
        canPickTime: false,
      }
    default: {
      const start = consultation.bookedTime?.startTime
      return {
        phase: 'booked',
        step: 1,
        title: start ? `Call ${formatCallTime(start)}` : 'Prepare for the call',
        description: `${consultation.requestedLength} minutes. Review the intake answers before you join.`,
        canPickTime: false,
      }
    }
  }
}

export function formatCallTime(date: Date) {
  return format(date, 'EEE, MMM d · h:mm a')
}
